// Lawyer Dashboard - Mock Data
// All mock data for the dashboard proof of concept

export interface StatItem {
  id: string;
  label: string;
  value: number;
  icon: string;
  trend?: {
    value: number;
    direction: 'up' | 'down';
  };
}

export interface ActionItem {
  id: number;
  type: 'document' | 'deadline' | 'court' | 'client';
  title: string;
  description: string;
  urgency: 'high' | 'medium' | 'low';
  dueIn: string;
  link: string;
}

export interface CourtDate {
  id: number;
  date: Date;
  courtName: string;
  room: string;
  caseRef: string;
  caseTitle: string;
}

export interface ActivityItem {
  id: number;
  type: 'document' | 'court' | 'case' | 'deadline';
  description: string;
  caseRef: string;
  timestamp: Date;
}

export interface CaseStats {
  draft: number;
  submitted: number;
  in_progress: number;
  resolved: number;
}

// Stats for the top row
export const mockStats: StatItem[] = [
  {
    id: 'active',
    label: 'Aktive Fälle',
    value: 24,
    icon: 'Briefcase',
    trend: { value: 12, direction: 'up' },
  },
  {
    id: 'pending',
    label: 'Ausstehende Prüfung',
    value: 5,
    icon: 'Clock',
  },
  {
    id: 'new',
    label: 'Neu diese Woche',
    value: 4,
    icon: 'TrendingUp',
    trend: { value: 33, direction: 'up' },
  },
  {
    id: 'deadlines',
    label: 'Bevorstehende Fristen',
    value: 3,
    icon: 'AlertCircle',
  },
];

// Priority action items
export const mockActions: ActionItem[] = [
  {
    id: 1,
    type: 'document',
    title: 'Neues Dokument prüfen',
    description: 'Bordkarte hochgeladen für Fall #EU-2024-0042',
    urgency: 'high',
    dueIn: '2 Std.',
    link: '/lawyer/cases/42',
  },
  {
    id: 2,
    type: 'deadline',
    title: 'Frist für Stellungnahme',
    description: 'Fall #EU-2024-0038 - Stellungnahme fällig',
    urgency: 'high',
    dueIn: '2 Tage',
    link: '/lawyer/cases/38',
  },
  {
    id: 3,
    type: 'court',
    title: 'Gerichtstermin naht',
    description: 'Amtsgericht Frankfurt - Fall #EU-2024-0038',
    urgency: 'medium',
    dueIn: '5 Tage',
    link: '/lawyer/cases/38',
  },
  {
    id: 4,
    type: 'client',
    title: 'Mandant wartet auf Antwort',
    description: 'Fall #EU-2024-0041 - Frage ausstehend',
    urgency: 'low',
    dueIn: '1 Tag',
    link: '/lawyer/cases/41',
  },
];

// Court dates for the calendar
export const mockCourtDates: CourtDate[] = [
  {
    id: 1,
    date: new Date(2025, 11, 18, 10, 0), // Dec 18, 2025 10:00
    courtName: 'Amtsgericht Frankfurt',
    room: 'Saal 204',
    caseRef: 'EU-2024-0038',
    caseTitle: 'Schmidt vs. Lufthansa',
  },
  {
    id: 2,
    date: new Date(2025, 11, 22, 14, 30), // Dec 22, 2025 14:30
    courtName: 'Amtsgericht München',
    room: 'Saal 112',
    caseRef: 'EU-2024-0029',
    caseTitle: 'Müller vs. Eurowings',
  },
  {
    id: 3,
    date: new Date(2026, 0, 8, 9, 0), // Jan 8, 2026 09:00
    courtName: 'Landgericht Berlin',
    room: 'Saal 301',
    caseRef: 'EU-2024-0015',
    caseTitle: 'Weber vs. Ryanair',
  },
  {
    id: 4,
    date: new Date(2025, 11, 18, 14, 0), // Dec 18, 2025 14:00 (same day as #1)
    courtName: 'Amtsgericht Frankfurt',
    room: 'Saal 108',
    caseRef: 'EU-2024-0044',
    caseTitle: 'Bauer vs. Condor',
  },
];

// Activity timeline items
export const mockActivity: ActivityItem[] = [
  {
    id: 1,
    type: 'document',
    description: 'Mandant hat Bordkarte hochgeladen',
    caseRef: 'EU-2024-0042',
    timestamp: new Date(Date.now() - 2 * 60 * 1000), // 2 min ago
  },
  {
    id: 2,
    type: 'court',
    description: 'Gerichtstermin bestätigt',
    caseRef: 'EU-2024-0038',
    timestamp: new Date(Date.now() - 60 * 60 * 1000), // 1h ago
  },
  {
    id: 3,
    type: 'case',
    description: 'Neuer Fall erstellt',
    caseRef: 'EU-2024-0043',
    timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000), // 3h ago
  },
  {
    id: 4,
    type: 'document',
    description: 'Dokument unterzeichnet',
    caseRef: 'EU-2024-0029',
    timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000), // 5h ago
  },
  {
    id: 5,
    type: 'deadline',
    description: 'Fristenerinnerung gesendet',
    caseRef: 'EU-2024-0015',
    timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000), // Yesterday
  },
  {
    id: 6,
    type: 'case',
    description: 'Fallstatus auf In Bearbeitung geändert',
    caseRef: 'EU-2024-0041',
    timestamp: new Date(Date.now() - 26 * 60 * 60 * 1000), // Yesterday
  },
  {
    id: 7,
    type: 'court',
    description: 'Gerichtstermin verschoben',
    caseRef: 'EU-2024-0029',
    timestamp: new Date(Date.now() - 48 * 60 * 60 * 1000), // 2 days ago
  },
];

// Case status breakdown
export const mockCaseStats: CaseStats = {
  draft: 3,
  submitted: 8,
  in_progress: 10,
  resolved: 3,
};

// Helper to get icon for activity type
export const activityTypeConfig = {
  document: {
    icon: 'FileText',
    color: 'blue',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-600',
  },
  court: {
    icon: 'Scale',
    color: 'purple',
    bgColor: 'bg-purple-100',
    textColor: 'text-purple-600',
  },
  case: {
    icon: 'Briefcase',
    color: 'green',
    bgColor: 'bg-green-100',
    textColor: 'text-green-600',
  },
  deadline: {
    icon: 'AlertCircle',
    color: 'amber',
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-600',
  },
};

// Helper to get urgency colors
export const urgencyConfig = {
  high: {
    border: 'border-l-red-500',
    bg: 'bg-red-50',
    badge: 'bg-red-100 text-red-700',
    dot: 'bg-red-500',
  },
  medium: {
    border: 'border-l-amber-500',
    bg: 'bg-amber-50',
    badge: 'bg-amber-100 text-amber-700',
    dot: 'bg-amber-500',
  },
  low: {
    border: 'border-l-blue-500',
    bg: 'bg-blue-50',
    badge: 'bg-blue-100 text-blue-700',
    dot: 'bg-blue-500',
  },
};

// Helper to get action type icon
export const actionTypeConfig = {
  document: { icon: 'FileText', label: 'Dokument' },
  deadline: { icon: 'Clock', label: 'Frist' },
  court: { icon: 'Scale', label: 'Gericht' },
  client: { icon: 'User', label: 'Mandant' },
};
